import useStore from "../../../store"
import { useEffect, useState } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { DateTime } from "luxon"
import DashboardContainer from "../../common/DashboardContainer"
import { Widget, WidgetBody, WidgetHeader } from "../../ui/Widget"
import InfoItem from "../../common/InfoItem"
import FieldAuditUnits from "./FieldAuditUnits/FieldAuditUnits"
import LotDetails from "./LotDetails/LotDetails"
import { SolidGreenButton, NeutralButton } from "../../common/Buttons"
import ActionConfirmDrawer from "../../common/ActionConfirmDrawer"
import { colors } from "../../../utilities/colors"
import Breadcrumbs from "../../common/Breadcrumbs"
import AuditUnitUploadQueue from "./AuditUnitUploadQueue"

export default function FieldAudit() {
  const navigate = useNavigate()
  const { fieldAuditId } = useParams()
  const {
    fieldAudit,
    fetchFieldAudit,
    completeFieldAudit,
    breadcrumbTrail,
    setBreadcrumbTrail,
    fieldAuditUnits,
  } = useStore()

  const [fetchingFieldAudit, setFetchingFieldAudit] = useState(true)
  const [isActionConfirmDrawerOpen, setIsActionConfirmDrawerOpen] = useState(false)

  const isVisible = !fetchingFieldAudit || fieldAudit?.id === fieldAuditId

  useEffect(() => {
    fetchFieldAudit(fieldAuditId,
      () => setFetchingFieldAudit(false)
    )
  }, [fieldAuditId])

  useEffect(() => {
    const newBreadcrumbTrail = [
      ...breadcrumbTrail,
      {
        label: `Dealer Audit`,
        path: `/field_audits/${fieldAuditId}`
      }
    ]
    setBreadcrumbTrail(newBreadcrumbTrail)

    return () => setBreadcrumbTrail(
      breadcrumbTrail.filter(
        (crumb) => crumb.label !== `Dealer Audit`
      )
    )
  }, [])

  const handleCompleteFieldAudit = async () => {
    setFetchingFieldAudit(true)
    await completeFieldAudit(
      fieldAuditId,
      () => navigate(`/dealers/${fieldAudit?.attributes.dealer.id}`)
    )
    setFetchingFieldAudit(false)
  }

  const isComplete = fieldAudit?.attributes['status-name'] === 'Completed'
  const auditType = fieldAudit?.attributes['audit-type']

  const headerTitle = () => {
    if (!isVisible) {
      return 'Audit'
    } else if (auditType === 'Condition Report') {
      return 'Condition Report'
    } else if (auditType === 'Audit Follow Up') {
      return 'Unit Audit'
    } else if (auditType === 'Field Audit') {
      return `${fieldAudit?.attributes.dealer.lotname} Audit`
    } else {
      return 'Audit'
    }
  }

  const allUnitsAudited = fieldAuditUnits?.data?.every(unit => unit.attributes['is-audited'])
  const isAbleToMarkComplete = auditType === 'Field Audit'
    ? allUnitsAudited &&
      fieldAudit?.attributes['current-inventory'] !== null &&
      !fieldAudit?.attributes?.dealer?.lot_photo_required
    : allUnitsAudited

  return(
    <DashboardContainer>
      <Breadcrumbs/>
      <Widget>
        <WidgetHeader
          title={headerTitle()}
          showSpinner={fetchingFieldAudit}
        />
        <WidgetBody
          styles="w-full grid grid-cols-2 lg:grid-cols-3"
          isVisible={isVisible}
        >
          <InfoItem title='Status'>
            {fieldAudit?.attributes['status-name']}
          </InfoItem>
          <InfoItem title='Time Started'>
            {DateTime.fromISO(fieldAudit?.attributes['time-started']).toLocaleString()}
          </InfoItem>
          <InfoItem title='Status by'>
            {fieldAudit?.attributes['status-by'].name}
          </InfoItem>
          <InfoItem title='Audit Type'>
            {auditType}
          </InfoItem>
            <div className="flex flex-col gap-2 justify-center col-span-3 my-2">
              {!isComplete &&
                <SolidGreenButton
                  text="Mark Audit Complete"
                  onClick={() => setIsActionConfirmDrawerOpen(true)}
                  styles="w-full"
                  disabled={isComplete || !isAbleToMarkComplete}
                />
              }
              <NeutralButton text="View Dealer" onClick={() => navigate(`/dealers/${fieldAudit?.attributes.dealer.id}`)} />
            </div>
        </WidgetBody>
      </Widget>

      <Widget>
        <WidgetHeader
          title="Dealer Information"
        />
        <WidgetBody
          styles="w-full grid grid-cols-2 lg:grid-cols-3"
          isVisible={isVisible}
        >
          <InfoItem title='Activated on'>
            {
              fieldAudit?.attributes.dealer['active_at'] ?
                DateTime.fromISO(fieldAudit?.attributes.dealer['active_at']).toLocaleString() :
                'Unavailable'
            }
          </InfoItem>
          <InfoItem title='Last Stock #'>
            {fieldAudit?.attributes.dealer['most_recent_stock_number']}
          </InfoItem>
        </WidgetBody>
      </Widget>


      {!isComplete &&
        <>
          {auditType === "Field Audit" &&
            <LotDetails
              fieldAudit={fieldAudit}
              isVisible={isVisible}
            />
          }

          <AuditUnitUploadQueue fieldAuditId={fieldAuditId} />

          <FieldAuditUnits fieldAuditId={fieldAuditId} />

          <ActionConfirmDrawer
            title="Mark Audit Complete"
            body={`Are you sure you want to mark this field audit as complete?`}
            onConfirm={handleCompleteFieldAudit}
            onCancel={() => setIsActionConfirmDrawerOpen(false)}
            isOpen={isActionConfirmDrawerOpen}
            onClose={() => setIsActionConfirmDrawerOpen(false)}
            bgColor={colors.black}
          />
        </>
      }
    </DashboardContainer>
  )
}